import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { formatNumber } from "~/ui-rtk/utils/format"
import { SOURCES_MAP, SOURCE_IDS } from "~/ui-rtk/constants/sources"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import type { CreativePickerItem } from "../hooks/useCreativePickerQuery"

const MEDIA_CHANNEL_TO_SOURCE: Record<string, string> = {
  facebook: SOURCE_IDS.FACEBOOK_ADS,
  meta: SOURCE_IDS.META,
  instagram: SOURCE_IDS.INSTAGRAM,
  tiktok: SOURCE_IDS.TIKTOK,
  tiktok_ads: SOURCE_IDS.TIKTOK_ADS,
  youtube: SOURCE_IDS.YOUTUBE,
  youtube_ads: SOURCE_IDS.YOUTUBE_ADS,
}

interface CreativePickerCardProps {
  item: CreativePickerItem
  isSelected: boolean
  onToggle: () => void
}

export default function CreativePickerCard({
  item,
  isSelected,
  onToggle,
}: CreativePickerCardProps) {
  const imageUrl =
    item.resolvedImageUrl || item.resolvedThumbnailUrl || item.thumbnail
  const sourceId =
    MEDIA_CHANNEL_TO_SOURCE[item.mediaChannel?.toLowerCase()] || SOURCE_IDS.META
  const source = SOURCES_MAP[sourceId]

  const metrics =
    item.type === "ad"
      ? [
          {
            label: "BV",
            value: formatNumber(item.brandValue, { compact: true }),
          },
          ...(item.spend
            ? [
                {
                  label: "Spend",
                  value: formatNumber(item.spend, {
                    compact: true,
                    currency: true,
                  }),
                },
              ]
            : []),
          ...(item.purchaseValue
            ? [
                {
                  label: "PV",
                  value: formatNumber(item.purchaseValue, {
                    compact: true,
                    currency: true,
                  }),
                },
              ]
            : []),
          ...(item.reach
            ? [
                {
                  label: "Reach",
                  value: formatNumber(item.reach, { compact: true }),
                },
              ]
            : []),
        ]
      : [
          {
            label: "BV",
            value: formatNumber(item.brandValue, { compact: true }),
          },
          ...(item.likes
            ? [
                {
                  label: "Likes",
                  value: formatNumber(item.likes, { compact: true }),
                },
              ]
            : []),
          ...(item.comments
            ? [
                {
                  label: "Comments",
                  value: formatNumber(item.comments, { compact: true }),
                },
              ]
            : []),
          ...(item.reach
            ? [
                {
                  label: "Reach",
                  value: formatNumber(item.reach, { compact: true }),
                },
              ]
            : []),
        ]

  return (
    <div
      role="button"
      tabIndex={0}
      className={`relative rounded-lg border cursor-pointer transition-all overflow-hidden ${
        isSelected
          ? "ring-2 ring-primary border-primary"
          : "border-system-gray-700/50 hover:border-system-gray-700"
      }`}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onToggle()
        }
      }}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-system-gray-900/80 relative">
        <Image
          src={imageUrl || ""}
          fallback={item.thumbnail}
          alt={item.title || "(untitled)"}
          loading="lazy"
          classes={{
            wrapper: "w-full h-full",
            image: "w-full h-full object-cover",
          }}
        />

        {/* Selection checkbox overlay */}
        {isSelected && (
          <div className="absolute top-2 right-2">
            <CheckCircleIcon className="w-6 h-6 text-primary drop-shadow-lg" />
          </div>
        )}

        {/* Media channel icon */}
        {source && (
          <div className="absolute bottom-2 left-2 w-5 h-5 rounded-full bg-system-gray-900/80 flex items-center justify-center">
            <img src={source.icon} alt={source.name} className="w-3.5 h-3.5" />
          </div>
        )}
      </div>

      {/* Card content */}
      <div className="p-2 bg-system-gray-900/50">
        <p className="text-xs text-basic-white truncate" title={item.title}>
          {item.title || "(untitled)"}
        </p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
          {metrics.slice(0, 4).map(m => (
            <div key={m.label} className="flex items-center justify-between">
              <span className="text-[10px] text-basic-light/40">{m.label}</span>
              <span className="text-[10px] text-basic-light/70 font-medium">
                {m.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
